import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { companyInfo } from '../data/mock';
import { sendUserThanks, sendOwnerNotification } from '../lib/emailjsClient';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : null;

const Contact = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm();

  const onSubmit = async (data) => {
    setSubmitError('');
    try {
      try {
        const logoUrl = `${window.location.origin}/MehtaAssociatesLogo.png`;
        await sendUserThanks({
          user_name: data.name,
          user_email: data.email,
          user_phone: data.phone,
          property_type: data.propertyType,
          property_location: data.location,
          message: data.message,
          logo_url: logoUrl
        });

        await sendOwnerNotification({
  name: data.name,
  email: data.email,
  phone: data.phone,
  property_type: data.propertyType,
  location: data.location,
  message: data.message
});

      } catch (emailErr) {
        console.warn('EmailJS send failed', emailErr);
      }

      setIsSubmitted(true);
      reset();
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (err) {
      console.error('Error submitting inquiry:', err);
      setSubmitError('Failed to submit inquiry. Please try again or call us directly.');
    }
  };

  const propertyTypes = [
    'Residential Property',
    'Agriculture Land',
    'Commercial Property',
    'Industrial Property',
    'Flat/Apartment',
    'Shop/Office Space',
    'Other'
  ];

  const gujaratCities = [
    "Ahmedabad","Surat","Vadodara","Rajkot","Gandhinagar","Bhavnagar","Jamnagar","Junagadh","Anand","Nadiad","Bharuch","Mehsana","Morbi","Surendranagar","Vapi","Navsari","Porbandar","Godhra","Veraval","Patan","Bhuj","Amreli","Palanpur","Valsad","Dahod","Botad","Deesa","Jetpur","Kalol","Gondal","Ankleshwar","Himmatnagar","Modasa","Vyara","Halol","Dholka","Dwarka","Somnath","Mandvi","Wadhwan","Upleta","Sanand","Khambhat","Umreth","Kadi","Unjha","Visnagar","Savarkundla","Talaja","Kodinar","Mangrol","Dhrol","Borsad","Petlad","Mundra","Padra","Rajpipla","Songadh","Chhota Udepur","Lunawada","Idar","Thangadh","Radhanpur","Dabhoi"
  ];

  return (
    <div className="bg-background pt-32 pb-20">
      <SEO
        title="Contact Mehta Associates — Property Valuation Bhavnagar"
        description="Contact Mehta Associates for government-approved property valuation in Bhavnagar and across Gujarat. Fast reports and bank-approved valuations."
        pathname="/contact"
        keywords={["contact Mehta Associates", "property valuer bhavnagar", "property valuation contact"]}
      />
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="heading-1 text-foreground mb-6">CONTACT US</h1>
          <p className="body-large text-[#666666] max-w-3xl mx-auto">
            Get in touch for fast, accurate, and government-approved property valuation services
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          {/* Contact Form */}
          <div className="bg-card p-8 border border-border">
            <h2 className="heading-4 text-foreground mb-6">Request Valuation</h2>
            
                {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-[#F4F7FB] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="text-foreground" size={40} />
                </div>
                <h3 className="heading-5 text-foreground mb-3">Thank You!</h3>
                <p className="body-medium text-[#666666]">
                  Your inquiry has been submitted. We'll contact you within 1 hour.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {submitError && (
                  <div className="bg-red-900/20 border border-red-500 text-red-300 px-4 py-3 rounded">
                    {submitError}
                  </div>
                )}

                <div>
                  <label htmlFor="name" className="caption text-[#888680] block mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    {...register('name', { required: 'Name is required', minLength: { value: 2, message: 'Enter at least 2 characters' } })}
                    disabled={isSubmitting}
                    className="w-full bg-card border border-border text-muted px-4 py-3 focus:border-primary focus:outline-none transition-colors disabled:opacity-50"
                    placeholder="Enter your full name"
                  />
                  {errors.name && <p className="text-sm text-red-600 mt-2">{errors.name.message}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="caption text-[#888680] block mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      {...register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' } })}
                      disabled={isSubmitting}
                      className="w-full bg-card border border-border text-muted px-4 py-3 focus:border-primary focus:outline-none transition-colors disabled:opacity-50"
                      placeholder="your@email.com"
                    />
                    {errors.email && <p className="text-sm text-red-600 mt-2">{errors.email.message}</p>}
                  </div>

                  <div>
                    <label htmlFor="phone" className="caption text-[#888680] block mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      {...register('phone', { required: 'Phone is required', minLength: { value: 7, message: 'Enter a valid phone' } })}
                      disabled={isSubmitting}
                      className="w-full bg-card border border-border text-muted px-4 py-3 focus:border-primary focus:outline-none transition-colors disabled:opacity-50"
                      placeholder="+91 XXXXX XXXXX"
                    />
                    {errors.phone && <p className="text-sm text-red-600 mt-2">{errors.phone.message}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="propertyType" className="caption text-[#888680] block mb-2">
                      Property Type *
                    </label>
                    <select
                      id="propertyType"
                      {...register('propertyType', { required: 'Please select a property type' })}
                      disabled={isSubmitting}
                      className="w-full bg-card border border-border text-muted px-4 py-3 focus:border-primary focus:outline-none transition-colors disabled:opacity-50"
                    >
                      <option value="">Select property type</option>
                      {propertyTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {errors.propertyType && <p className="text-sm text-red-600 mt-2">{errors.propertyType.message}</p>}
                  </div>

                  <div>
                    <label htmlFor="location" className="caption text-muted block mb-2">
                      City (in Gujarat) *
                    </label>
                    <select
                      id="location"
                      {...register('location', { required: 'Please select a city' })}
                      disabled={isSubmitting}
                      className="w-full bg-card border border-border text-muted px-4 py-3 focus:border-primary focus:outline-none transition-colors disabled:opacity-50"
                    >
                      <option value="">Select city</option>
                      {gujaratCities.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    {errors.location && <p className="text-sm text-red-600 mt-2">{errors.location.message}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="caption text-[#888680] block mb-2">
                    Additional Details
                  </label>
                  <textarea
                    id="message"
                    {...register('message')}
                    rows="4"
                    disabled={isSubmitting}
                    className="w-full bg-card border border-border text-muted px-4 py-3 focus:border-primary focus:outline-none transition-colors resize-none disabled:opacity-50"
                    placeholder="Any specific requirements or questions..."
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send size={18} />
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </button>

                <p className="body-small text-[#666666] text-center">
                  We'll respond within 1 hour during business hours
                </p>
              </form>
            )}
          </div>

          {/* Contact Information */}
          <div>
            <div className="bg-card p-8 border border-border mb-8">
              <h3 className="heading-5 text-foreground mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F4F7FB] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-foreground" size={24} />
                  </div>
                  <div>
                    <h4 className="body-medium text-foreground mb-2">Office Address</h4>
                    <p className="body-small text-[#666666]">{companyInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F4F7FB] rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="text-foreground" size={24} />
                  </div>
                  <div>
                    <h4 className="body-medium text-foreground mb-2">Phone Numbers</h4>
                    <p className="body-small text-[#888680]">
                      <a href={`tel:${companyInfo.mobile}`} className="hover:text-accent transition-colors">
                        {companyInfo.mobile}
                      </a>
                    </p>
                    {/* <p className="body-small text-[#888680] mt-1">
                      <a href={`tel:${companyInfo.alternateMobile}`} className="hover:text-accent transition-colors">
                        {companyInfo.alternateMobile}
                      </a>
                    </p> */}
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F4F7FB] rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-foreground" size={24} />
                  </div>
                  <div>
                    <h4 className="body-medium text-foreground mb-2">Email Address</h4>
                    <p className="body-small text-[#666666]">
                      <a href={`mailto:${companyInfo.email}`} className="hover:text-accent transition-colors">
                        {companyInfo.email}
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F4F7FB] rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="text-foreground" size={24} />
                  </div>
                  <div>
                    <h4 className="body-medium text-foreground mb-2">Business Hours</h4>
                    <p className="body-small text-[#666666]">Monday - Saturday: 9:00 AM - 7:00 PM</p>
                    <p className="body-small text-[#666666]">Sunday: By Appointment</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="bg-card border border-border h-80 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="text-foreground mx-auto mb-4" size={48} />
                <p className="body-medium text-[#666666]">Bhavnagar, Gujarat</p>
                <p className="body-small text-[#666666] mt-2">Map integration available</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Contact */}
        <div className="bg-card p-12 border border-border text-center">
          <h2 className="heading-4 text-foreground mb-6">Need Immediate Assistance?</h2>
          <p className="body-medium text-[#666666] mb-8 max-w-2xl mx-auto">
            For urgent valuation requests or queries, call us directly
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.mobile}`} className="btn-primary">
              Call {companyInfo.mobile}
            </a>
            <a href={`https://wa.me/91${companyInfo.mobile.replace(/\s/g, '')}`} target="_blank" rel="noopener noreferrer" className="btn-secondary">
              WhatsApp Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
